import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, LogIn } from "lucide-react";
import { Link } from "react-router-dom";
import { AIRBNB_URL, PROPERTY } from "@/data/property";

const NAV = [
  { label: "Gallery", href: "#gallery" },
  { label: "Amenities", href: "#amenities" },
  { label: "Sleeping", href: "#sleeping" },
  { label: "Location", href: "#location" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass shadow-[0_8px_30px_rgba(14,42,71,0.08)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[70px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          className={`hidden md:inline-block font-display font-semibold tracking-tight text-lg transition-colors ${
            scrolled ? "text-[#0E2A47]" : "text-white"
          }`}
        >
          {PROPERTY.name}
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm font-body transition-colors hover:text-[#1FB6C9] ${
                scrolled ? "text-[#0E2A47]" : "text-white/90"
              }`}
            >
              {n.label}
            </a>
          ))}
          <Link
            to="/weddings"
            data-testid="nav-weddings"
            className={`text-sm font-body transition-colors hover:text-[#1FB6C9] ${scrolled ? "text-[#0E2A47]" : "text-white/90"}`}
          >
            Weddings
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="header-guest-login-btn"
            className={`hidden md:inline-flex items-center gap-1.5 text-sm font-body transition-colors ${scrolled ? "text-[#0E2A47] hover:text-[#1FB6C9]" : "text-white/90 hover:text-white"}`}
          >
            <LogIn size={14} /> Guest guide
          </Link>
          <a
            href={AIRBNB_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-[#1FB6C9] px-5 py-2.5 text-sm font-display font-medium text-white shadow-lg shadow-[#1FB6C9]/30 transition-transform duration-300 hover:-translate-y-0.5"
          >
            Book Now
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className={`lg:hidden p-2 rounded-full ${scrolled ? "text-[#0E2A47]" : "text-white"}`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden glass border-t border-white/30"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-[#0E2A47] font-body py-1"
              >
                {n.label}
              </a>
            ))}
            <Link to="/weddings" onClick={() => setOpen(false)} className="text-[#0E2A47] font-body py-1">Weddings</Link>
            <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-guest-login-btn" className="text-[#0E2A47] font-body py-1 inline-flex items-center gap-2">
              <LogIn size={14} /> Guest guide
            </Link>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
